import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ChromePicker } from 'react-color';
import {
  Button,
  Col,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { addFlashMessage } from '../../../../../actions/App';
import { fetchUserProfile } from '../../../../../actions/Auth';

import { APIupdateSettings } from '../../../../../api/Settings';

import { userHasFeature } from '../../../../../helpers/Features';
import {
  defaultLogoUrl,
  defaultShareLogoUrl,
} from '../../../../../helpers/Settings';

import SettingsEditButtons from '../../EditButtons';
import LogoUploader from '../../../LogoUploader';
import TrialNotice from '../../../../TrialNotice';
import UpgradePlanButton from '../../../../UpgradePlanButton';

const SettingsBranding = ({
  dispatch,
  settings,
  client,
}) => {
  const [unsaved, setUnsaved] = useState(false);
  const [color, setColor] = useState(settings.color);
  const [logoUrl, setLogoUrl] = useState(client.logo_url);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(client.logo_url);
  const [shareLogoUrl, setShareLogoUrl] = useState(client.share_logo_url);
  const [shareLogoPreviewUrl, setShareLogoPreviewUrl] = useState(client.share_logo_url);
  const [colorPickerOpened, setColorPickerOpened] = useState(false);
  const [isLogoDirty, setIsLogoDirty] = useState(false);
  const [isShareLogoDirty, setIsShareLogoDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const intl = useIntl();

  const handleSaveSettings = () => {
    setSaving(true);
    const settingsToUpdate = {};
    settingsToUpdate.color = color;
    if (isLogoDirty) {
      settingsToUpdate.logo_url = logoUrl;
    }
    if (isShareLogoDirty) {
      settingsToUpdate.share_logo_url = shareLogoUrl;
    }
    APIupdateSettings(settingsToUpdate).then(() => {
      dispatch(fetchUserProfile()).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Changes have been saved' }),
        });
        setSaving(false);
        setIsLogoDirty(false);
        setIsShareLogoDirty(false);
        setUnsaved(false);
        if (settings.color !== color) {
          window.location.reload();
        }
      });
    });
  };

  return (
    <>
      <div className="settings-inner-content">
        <Row>
          <Col>
            <h3>{intl.formatMessage({ defaultMessage: 'Branding' })}</h3>
          </Col>
        </Row>
        <TrialNotice feature="branding" className="pl-0" />
        <Row className="color-row">
          <Col xl={6}>
            <div className="color-content">
              <h4>
                {intl.formatMessage({ defaultMessage: 'Color' })}
                <UpgradePlanButton
                  feature="branding"
                />
              </h4>
            </div>
            <div className="settings-section">
              <div className="color-button">
                <div
                  onClick={() => {
                    if (userHasFeature('branding')) {
                      setColorPickerOpened(true);
                    }
                  }}
                  aria-hidden="true"
                >
                  <div className="color-button-content color-button-content-big" style={{ backgroundColor: color }} />
                </div>
                {colorPickerOpened && (
                  <div className="color-picker-popover">
                    <div className="color-picker-cover" onClick={() => { setColorPickerOpened(false); }} aria-hidden="true" />
                    <ChromePicker
                      color={color}
                      disableAlpha={true}
                      onChange={(c) => {
                        setColor(c.hex);
                        setUnsaved(true);
                      }}
                    />
                  </div>
                )}
              </div>
              <div>
                <h5>
                  {intl.formatMessage({
                    defaultMessage: 'Primary color',
                  })}
                </h5>
                <p>
                  {intl.formatMessage({
                    defaultMessage: 'Select a primary color to use for your workspace links and log in screen',
                  })}
                </p>
              </div>
            </div>
          </Col>
        </Row>
        <hr />
        <Row className="logo-row">
          <Col xl={6}>
            <h4>
              {intl.formatMessage({ defaultMessage: 'Logo' })}
              <UpgradePlanButton
                feature="branding"
              />
            </h4>
            <p>
              {intl.formatMessage({
                defaultMessage: `Upload a primary logo for your workspace that will be used in
                the sidebar and on the login screen`,
              })}
            </p>
            <div className="image-content-wrapper">
              <div className="image-container">
                <div className="image-wrapper">
                  <img src={logoPreviewUrl} alt="Logo" />
                </div>
              </div>
              {userHasFeature('branding') && (
                <div className="logo-buttons">
                  <LogoUploader
                    name="logo-uploader"
                    suffix="logo"
                    handleChangeImage={(url) => {
                      setLogoUrl(url);
                      setIsLogoDirty(true);
                      setUnsaved(true);
                    }}
                    handleChangePreview={(url) => { setLogoPreviewUrl(url); }}
                  />
                  <Button
                    id="PrimaryLogoRemoveButton"
                    onClick={() => {
                      setIsLogoDirty(true);
                      setLogoUrl(null);
                      setLogoPreviewUrl(defaultLogoUrl);
                      setUnsaved(true);
                    }}
                    color="link"
                  >
                    <i className="far fa-trash" />
                  </Button>
                  <UncontrolledTooltip target="PrimaryLogoRemoveButton" placement="bottom">
                    {intl.formatMessage({ defaultMessage: 'Remove' })}
                  </UncontrolledTooltip>
                </div>
              )}
            </div>
          </Col>
          <Col xl={6}>
            <h4>
              {intl.formatMessage({ defaultMessage: 'Logo for shares' })}
              <UpgradePlanButton
                feature="branding"
              />
            </h4>
            <p>
              {intl.formatMessage({
                defaultMessage: `Upload a secondary logo that will appear on all
                share links. Horizontal orientation is suggested`,
              })}
            </p>
            <div className="image-content-wrapper">
              <div className="image-container">
                <div className="image-wrapper">
                  <img
                    src={shareLogoPreviewUrl}
                    alt={intl.formatMessage({ defaultMessage: 'Logo for shares' })}
                  />
                </div>
              </div>
              {userHasFeature('branding') && (
                <div className="logo-buttons">
                  <LogoUploader
                    name="share-logo-uploader"
                    suffix="share-logo"
                    handleChangeImage={(url) => {
                      setShareLogoUrl(url);
                      setIsShareLogoDirty(true);
                      setUnsaved(true);
                    }}
                    handleChangePreview={(url) => { setShareLogoPreviewUrl(url); }}
                  />
                  <Button
                    id="ShareLogoRemoveButton"
                    onClick={() => {
                      setIsShareLogoDirty(true);
                      setShareLogoUrl(null);
                      setShareLogoPreviewUrl(defaultShareLogoUrl);
                      setUnsaved(true);
                    }}
                    color="link"
                  >
                    <i className="far fa-trash fa-fw" />
                  </Button>
                  <UncontrolledTooltip target="ShareLogoRemoveButton" placement="bottom">
                    {intl.formatMessage({ defaultMessage: 'Remove' })}
                  </UncontrolledTooltip>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <SettingsEditButtons
        saving={saving}
        handleSave={handleSaveSettings}
        unsaved={unsaved}
      />
    </>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
  settings: state.App.settings,
});

SettingsBranding.propTypes = {
  dispatch: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(SettingsBranding);
